"use client";

type AnalyticsPayload = {
  event: string;
  metadata?: Record<string, unknown>;
  /**
   * Set true for events fired immediately before navigation (router.push,
   * <Link> click, full page reload). Forces PostHog to send the event
   * without batching so it isn't dropped when the page unmounts.
   */
  sendInstantly?: boolean;
};

// window.posthog is declared in src/types/window.d.ts (canonical source).
// This block adds the GA4-only extensions (gtag, dataLayer).
declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

export function trackEvent({ event, metadata, sendInstantly }: AnalyticsPayload) {
  if (typeof window === "undefined") {
    return;
  }

  const props = {
    event_category: "marketing",
    ...metadata,
  };

  if (window.gtag) {
    window.gtag("event", event, props);
  }

  if (window.dataLayer) {
    window.dataLayer.push({
      event,
      ...props,
    });
  }

  if (window.posthog) {
    window.posthog.capture(
      event,
      metadata,
      sendInstantly ? { send_instantly: true } : undefined,
    );
  } else if (pendingPosthogEvents.length < MAX_PENDING_POSTHOG_EVENTS) {
    // PostHog is imported lazily by PosthogProvider, whose init effect runs
    // AFTER its children's effects. Anything a page fires on mount (e.g.
    // hc_page_viewed on /free-health-check) used to be dropped here, which
    // made the top of every funnel read as zero. Hold it until init.
    pendingPosthogEvents.push({ event, metadata, sendInstantly });
  }
}

/**
 * Events captured before PostHog finished loading. Capped: when PostHog never
 * loads (no key, automation, blocked script) the queue must not grow forever.
 */
const MAX_PENDING_POSTHOG_EVENTS = 50;
const pendingPosthogEvents: AnalyticsPayload[] = [];

/**
 * Send every event that {@link trackEvent} held while PostHog was loading.
 * Called by PosthogProvider once `window.posthog` exists.
 */
export function flushPendingAnalytics() {
  if (typeof window === "undefined" || !window.posthog) return;
  const queued = pendingPosthogEvents.splice(0, pendingPosthogEvents.length);
  for (const { event, metadata, sendInstantly } of queued) {
    window.posthog.capture(
      event,
      metadata,
      sendInstantly ? { send_instantly: true } : undefined,
    );
  }
}

/** Test-only: number of events waiting for PostHog. */
export function pendingAnalyticsCount() {
  return pendingPosthogEvents.length;
}

/**
 * Track conversion funnel events for GA4
 * Uses recommended GA4 event names where applicable:
 * - sign_up, begin_checkout, purchase (GA4 recommended)
 * - onboarding_started, onboarding_complete, platform_connected (custom)
 *
 * Defaults `sendInstantly: true` because conversion events almost always
 * fire right before a navigation (post-signup redirect, etc.).
 */
export function trackConversionEvent(
  eventName: string,
  params?: Record<string, unknown>,
  options?: { sendInstantly?: boolean },
) {
  trackEvent({
    event: eventName,
    metadata: {
      event_category: "conversion",
      ...params,
    },
    sendInstantly: options?.sendInstantly ?? true,
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// Activation & "aha-moment" taxonomy (single source of truth)
//
// These constants codify the post-signup engagement funnel so the aha-moment,
// the activation steps, and the idle/win-back signal are all defined in ONE
// place rather than scattered as ad-hoc string literals across call sites.
//
// Funnel (ordered):
//   signup
//     → ACTIVATION.PLATFORM_CONNECTED   (org connects Salesforce/HubSpot/…)
//     → ACTIVATION.HEALTH_CHECK_RUN     (first health check executed)
//     → ACTIVATION.FIRST_FINDING_VIEWED ← AHA_MOMENT (user sees real value)
//     → ACTIVATION.FIRST_FIX_APPLIED    (user acts on a finding)
//     → ACTIVATION.FIRST_AI_CONSULT     (user asks the AI Consultant)
//
// After activation, an org that goes quiet for IDLE_THRESHOLD_DAYS becomes a
// win-back candidate (see /api/cron/win-back). RE_ENGAGED fires when such a
// user returns. Keep this taxonomy in lock-step with win-back-data.ts.
// ──────────────────────────────────────────────────────────────────────────────

export const ACTIVATION_EVENTS = {
  /** Org connected its first platform — the activation gate. */
  PLATFORM_CONNECTED: "activation_platform_connected",
  /** First health check executed against a connected org. */
  HEALTH_CHECK_RUN: "activation_health_check_run",
  /** First real finding viewed — the codified aha-moment. */
  FIRST_FINDING_VIEWED: "aha_first_finding_viewed",
  /** First remediation/fix applied to a finding. */
  FIRST_FIX_APPLIED: "aha_first_fix_applied",
  /** First question asked of the AI Consultant. */
  FIRST_AI_CONSULT: "activation_first_ai_consult",
  /** An idle (connected-but-dormant) user returned to the app. */
  RE_ENGAGED: "activation_re_engaged",
} as const;

export type ActivationEvent =
  (typeof ACTIVATION_EVENTS)[keyof typeof ACTIVATION_EVENTS];

/**
 * The single, codified "aha-moment": the first time a user views a real
 * finding from their own org. This is the event the activation funnel and the
 * win-back idle signal both pivot around.
 */
export const AHA_MOMENT: ActivationEvent = ACTIVATION_EVENTS.FIRST_FINDING_VIEWED;

/**
 * Idle threshold (days) that defines a "disengaged" connected org. Mirrors
 * DEFAULT_IDLE_DAYS in win-back-data.ts — keep the two in sync.
 */
export const IDLE_THRESHOLD_DAYS = 14;

/**
 * Fire an activation/aha funnel event. Thin wrapper over {@link trackEvent}
 * that stamps a consistent `event_category: "activation"` and an
 * `activation_step` so the funnel is queryable in PostHog/GA4 as one cohort.
 *
 * Prefer passing a value from {@link ACTIVATION_EVENTS} so the taxonomy stays
 * centralized.
 *
 * @example trackActivation(ACTIVATION_EVENTS.FIRST_FINDING_VIEWED, { findingId })
 */
export function trackActivation(
  step: ActivationEvent,
  params?: Record<string, unknown>,
  options?: { sendInstantly?: boolean },
) {
  trackEvent({
    event: step,
    metadata: {
      event_category: "activation",
      activation_step: step,
      is_aha_moment: step === AHA_MOMENT,
      ...params,
    },
    sendInstantly: options?.sendInstantly ?? false,
  });
}

/**
 * Convenience helper for the codified aha-moment (first finding viewed). Equivalent
 * to `trackActivation(AHA_MOMENT, params)` but reads clearly at the call site.
 */
export function trackAhaMoment(params?: Record<string, unknown>) {
  trackActivation(AHA_MOMENT, params);
}
